import React, {useCallback, useEffect} from 'react';
import {ProductionCostBusProvidedValuesEntry} from '@/Pages/ProductionCost/store/types';
import {useBusPartner} from '@/Pages/PartnerCosts/hooks/useBusPartner';
import {useCountries} from '@/Pages/Countries/hooks/useCountries';
import useVehicleTypeCostEntries from '@/Pages/ProductionCost/components/DataImportStepTwo/hooks/useGetVehicleTypeCostEntriesQuery';
import VehicleTypeCostsSelect from './VehicleTypeCostsSelect/VehicleTypeCostsSelect';
import {VehicleTypeProps} from './VehicleType.types';

const VehicleType: React.FC<VehicleTypeProps> = ({
  busPartnerId,
  vehicleId,
  busPartnerBusTypeCostEntryId,
  countryBusTypeCostEntryId,
  errors,
  onUpdateBusValues
}) => {
  const busPartner = useBusPartner(busPartnerId);
  const {countries} = useCountries();

  const {vehicleTypeCostEntries, loading} =
    useVehicleTypeCostEntries(busPartner);

  const selectedBusTypeCostEntryId =
    busPartnerBusTypeCostEntryId ?? countryBusTypeCostEntryId;

  const handleSelectChange = useCallback(
    (updates: Partial<ProductionCostBusProvidedValuesEntry>) => {
      onUpdateBusValues(updates);
    },
    [onUpdateBusValues]
  );

  useEffect(() => {
    if (loading || !vehicleTypeCostEntries) return;

    const {busPartnerBusTypeCostEntries, countryBusTypeCostEntries} =
      vehicleTypeCostEntries;

    const allOptions = [
      ...busPartnerBusTypeCostEntries,
      ...countryBusTypeCostEntries
    ];

    const selectedStillExists =
      !!selectedBusTypeCostEntryId &&
      allOptions.some(({id}) => id === selectedBusTypeCostEntryId);

    // keep existing valid selection
    if (selectedStillExists) return;

    // try to preselect
    let busPartnerBusTypeCostEntryId: string | undefined = undefined;
    let countryBusTypeCostEntryId: string | undefined = undefined;

    if (allOptions.length === 1) {
      const onlyOption = allOptions[0];

      const isBusPartnerEntry = busPartnerBusTypeCostEntries.some(
        e => e.id === onlyOption.id
      );

      if (isBusPartnerEntry) {
        busPartnerBusTypeCostEntryId = onlyOption.id;
      } else {
        countryBusTypeCostEntryId = onlyOption.id;
      }
    }

    handleSelectChange({
      busPartnerBusTypeCostEntryId,
      countryBusTypeCostEntryId
    });
  }, [
    handleSelectChange,
    loading,
    selectedBusTypeCostEntryId,
    vehicleTypeCostEntries
  ]);

  return (
    <div data-testid="line-vehicle-vehicle-type">
      <VehicleTypeCostsSelect
        busPartner={busPartner}
        countries={countries}
        selectedBusTypeCostEntryId={selectedBusTypeCostEntryId}
        vehicleId={vehicleId}
        vehicleTypeCostEntries={vehicleTypeCostEntries}
        errors={errors}
        onChange={handleSelectChange}
      />
    </div>
  );
};

export default React.memo(VehicleType);
